import { Trash2, Pencil, Repeat, MousePointer, Check, Play } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import DeleteMessage from 'features/podcast/modals/DeleteMessage';
import { useEffect, useRef, useState } from 'react';
import RegenerateDialogue from './RegenerateDialogue';
import EditableText from './EditableText';
import { Button } from 'components/ui/button';
import AudioCard from './AudioCard';
import { useDispatch } from 'react-redux';
import { updateDialogue, regenDialogue } from 'store/slices/podcastResponse';
import toast from 'react-hot-toast';
import useIsMobile from 'hooks/useIsMobile';
import { podcastServices } from 'features/podcast/services/podcastApi';
import { useQuery } from '@tanstack/react-query';
import { voiceSelectionService } from '../../services/voiceSelectApi';

interface dialogueCardProps {
  character_name: string;
  voice_name: string;
  dialogue: string;
  role: 'host' | 'guest';
  preview_url: string;
  index: number;
  presigned_preview_url: string;
}

// Declare window property for TypeScript
declare global {
  interface Window {
    currentPlayingAudio: HTMLAudioElement | null;
  }
}

const DialogueCard = ({
  character_name,
  dialogue,
  role,
  voice_name,
  index,
  presigned_preview_url,
}: dialogueCardProps) => {
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openRegenerate, setopenRegenerate] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [audioPlay, setAudioPlay] = useState<boolean>(false);
  const [text, setText] = useState(dialogue);
  const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  // audio playing and time configs
  const [percentagePlayed, setPercentagePlayed] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Then inside the component, update the audio handling code
  // to avoid overlapping play and pause
  const [debounceTimeout, setDebounceTimeout] = useState<number | NodeJS.Timeout | null>(null);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  // Fetch voices data
  const { data: voices = [] } = useQuery({
    queryKey: ['Voices'],
    queryFn: voiceSelectionService.getVoices,
  });

  // Find the matching voice and get its image URL
  const matchedVoice = voices.find(voice => voice.name === voice_name);
  const voiceImageUrl = matchedVoice?.url || "https://img.freepik.com/premium-vector/avatar-profile-icon-flat-style-female-user-profile-vector-illustration-isolated-background-women-profile-sign-business-concept_157943-38866.jpg?semt=ais_hybrid";

  const handlePlay = (url: string) => {
    // creating the ref instance if it does not exist
    if (!audioRef.current) {
      audioRef.current = new Audio(url);

      // Update current time and percentage during playback
      audioRef.current.addEventListener('timeupdate', () => {
        if (audioRef.current) {
          const current = audioRef.current.currentTime;
          setPercentagePlayed((current / audioRef.current.duration) * 100); // Calculate percentage
        }
      });

      // used ref as we can access the different stages which wont be possible in const audio = new Audio()
      audioRef.current.addEventListener('play', () => setAudioPlay(true));
      audioRef.current.addEventListener('pause', () => setAudioPlay(false));
      audioRef.current.addEventListener('ended', () => {
        setAudioPlay(false);
        //reset
        setPercentagePlayed(0);
      });
    }

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      
      // Play with error handling
      const playPromise = audioRef.current.play();
      
      // Modern browsers return a promise from play()
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Auto-play was prevented or play() was interrupted
        });
      }
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      // Only attempt to pause if audio is actually playing
      if (!audioRef.current.paused) {
        try {
          audioRef.current.pause();
        } catch (error) {
          // Error handling for pause operation
        }
      }
    }
  };

  // to keep audioplay always true in mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < 640) {
        setAudioPlay(true); // Enable audioPlay for small screens
      }
    };

    checkScreenSize(); // Run on mount

    window.addEventListener('resize', checkScreenSize); // Listen for resize events
    return () => window.removeEventListener('resize', checkScreenSize); // Cleanup
  }, []);

  // Special handling for mobile edit mode
  useEffect(() => {
    if (isEditing && isMobile) {
      // Make sure textarea is properly focused on mobile
      const textarea = document.querySelector('textarea[aria-label="edit-dialogue"]');
      if (textarea) {
        setTimeout(() => {
          (textarea as HTMLTextAreaElement).focus();
        }, 100);
      }
    }
  }, [isEditing, isMobile]);

  const handleMouseEnter = (url: string) => {
    if (isMobile) return;

    setIsHovering(true);
    
    // Clear any pending pause timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout as NodeJS.Timeout);
      setDebounceTimeout(null);
    }
    
    // Use a small delay before playing to avoid race conditions
    setTimeout(() => {
      // Pause any currently playing audio from other dialogue cards
      if (typeof window !== 'undefined' && window.currentPlayingAudio && 
          window.currentPlayingAudio !== audioRef.current) {
        try {
          window.currentPlayingAudio.pause();
        } catch (error) {
          // Error handling for pause operation
        }
      }
      
      handlePlay(url);
      
      // Store the current audio reference globally
      if (typeof window !== 'undefined') {
        window.currentPlayingAudio = audioRef.current;
      }
    }, 50);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    
    // Add a small delay before pausing to avoid race conditions
    const timeout = setTimeout(() => {
      pauseAudio();
    }, 50);
    
    setDebounceTimeout(timeout);
  };

  // Ensure cleanup on component unmount
  useEffect(() => {
    // Initialize the global audio reference if it doesn't exist
    if (typeof window !== 'undefined' && window.currentPlayingAudio === undefined) {
      window.currentPlayingAudio = null;
    }

    return () => {
      // Clean up audio on component unmount
      if (audioRef.current) {
        audioRef.current.pause();
        if (typeof window !== 'undefined' && window.currentPlayingAudio === audioRef.current) {
          window.currentPlayingAudio = null;
        }
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout as NodeJS.Timeout);
      }
    };
  }, [debounceTimeout]);

  const handleEditMessage = async (index: number, text: string) => {
    try {
      // Set loading state
      setIsEditLoading(true);
      // Show loading toast
      const loadingToast = toast.loading('Editing dialogue...');
      
      // Call the API to edit the dialogue
      const response = await podcastServices.editDialogue({
        dialogue: text,
        voice_name: voice_name
      });
      
      // Update the store with the edited dialogue and new audio URL
      if (response.success) {
        dispatch(regenDialogue({ 
          index, 
          text, 
          preview_url: response.data.preview_url,
          presigned_preview_url: response.data.presigned_preview_url
        }));
        
        // Dismiss the loading toast and show success
        toast.dismiss(loadingToast);
        toast.success('Dialogue edited!');
        setIsEditing(false);
      } else {
        // If the API call wasn't successful
        toast.dismiss(loadingToast);
        toast.error(response.message || 'Failed to edit dialogue');
      }
    } catch (error) {
      // If there was an error with the API call
      console.error('Error editing dialogue:', error);
      toast.error('An error occurred while editing the dialogue');
      // Fallback to local update only
      dispatch(updateDialogue({ index, text }));
      setIsEditing(false);
    } finally {
      setIsEditLoading(false);
    }
  };

  return (
    <div
      className={`min-h-fit flex flex-col mx-auto gap-3 w-full ${role === 'host' ? 'items-start' : 'items-end'}`}
    >
      {/* container for dialogue options and details*/}
      <div
        className={`flex ${role === 'host' ? 'flex-row' : 'flex-row-reverse'} items-center`}
      >
        <div className="sm:w-[860px] w-[320px] h-full">
          {/* name and buttons */}
          <div
            className={`flex ${role === 'host' ? 'flex-row' : 'flex-row-reverse'} justify-between relative mb-2`}
          >
            {/* name */}
            <div
              className={`flex ${role === 'host' ? 'flex-row' : 'flex-row-reverse'} flex-row items-center justify-start gap-2`}
            >
              <div className="rounded-full">
                <img
                  src={voiceImageUrl}
                  alt={`${character_name} avatar`}
                  className="rounded-full sm:w-14 sm:h-14 w-8 h-8 object-cover"
                  onError={(e) => {
                    // Fallback to default avatar if the voice image fails to load
                    e.currentTarget.src = "https://img.freepik.com/premium-vector/avatar-profile-icon-flat-style-female-user-profile-vector-illustration-isolated-background-women-profile-sign-business-concept_157943-38866.jpg?semt=ais_hybrid";
                  }}
                />
              </div>
              <h1 className="text-left text-text-primary font-semibold text-[14px] sm:text-xl">
                {character_name} ({role === 'guest' ? 'Guest' : 'Host'})
              </h1>
            </div>
            {/* buttons */}
            <div className="flex items-end mb-0 sm:mb-2 gap-1">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      title="delete"
                      className={`text-text-primary rounded-[4px] hover:text-primary p-2 ${openDelete && ' bg-background-main text-primary'}`}
                      onClick={() => {
                        if (openRegenerate) {
                          setopenRegenerate(false);
                        } else if (isEditing) {
                          setIsEditing(false);
                        }
                        setOpenDelete((prev) => !prev);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="w-[60px] text-center rounded-[4px] h-[26px] bg-ackground text-primary"
                      sideOffset={5}
                    >
                      Delete
                      <Tooltip.Arrow className="TooltipArrow" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                {openDelete && (
                  <DeleteMessage setOpenDelete={setOpenDelete} index={index} />
                )}
              </Tooltip.Provider>
              <button
                type="button"
                title="edit"
                className={`text-text-primary hover:text-primary p-2 rounded-[4px] ${isEditing && 'bg-background-main text-primary'}`}
                onClick={() => {
                  if (openDelete) {
                    setOpenDelete(false);
                  } else if (openRegenerate) {
                    setopenRegenerate(false);
                  }
                  setIsEditing((prev) => !prev);
                }}
              >
                {isEditing ? <Check size={16} /> : <Pencil size={16} />}
              </button>
              <button
                type="button"
                title="regenerate"
                className={`text-text-primary hover:text-[#846CEC] p-2 rounded-[4px] ${openRegenerate && 'bg-primaryAccent-5 text-[#846CEC]'}`}
                onClick={() => {
                  if (openDelete) {
                    setOpenDelete(false);
                  } else if (isEditing) {
                    setIsEditing(false);
                  }
                  setopenRegenerate((prev) => !prev);
                }}
              >
                <Repeat size={16} />
              </button>
              {openRegenerate && (
                <RegenerateDialogue
                  setOpenRegenerate={setopenRegenerate}
                  regenPart={text}
                  voice_name={voice_name}
                  index={index}
                />
              )}
            </div>
          </div>

          {/* content div */}
          <div
            className={`min-h-fit overflow-y-auto rounded-lg py-8 px-10 
              ${role === 'guest' 
                ? 'bg-[#f3f0ff] text-text-primary' 
                : 'bg-[#333333] text-white'} 
              border-2 relative
              ${isEditing || openRegenerate || openDelete 
                ? 'border-primary' 
                : isHovering
                  ? 'border-[#846CEC]'
                  : 'border-transparent hover:border-[#846CEC] transition-colors duration-200'}`}
            onMouseEnter={() =>
              !isEditing && handleMouseEnter(presigned_preview_url)
            }
            onMouseLeave={() => !isEditing && handleMouseLeave()}
          >
            {isHovering && !isEditing && (
              <div className="absolute right-2 top-2 w-8 h-8 flex items-center justify-center rounded-full bg-[#846CEC] bg-opacity-20">
                <Play color="#846CEC" size={16} />
              </div>
            )}
            <div className="relative">
              {/* "Hover to play" text has been removed to eliminate the purple line */}
              <EditableText 
                isEditing={isEditing} 
                text={text} 
                setText={setText} 
                textColor={role === 'host' ? 'text-white' : 'text-black'}
              />
            </div>
          </div>
        </div>

        {/* audio play */}
        {index === 0 && (
          <div
            className={`${role === 'host' ? 'ml-4' : 'mr-4'} mt-10 sm:block hidden`}
          >
            {/* icon */}
            <MousePointer color="#846CEC" size={16} />
            {/* button */}
            <div className="pl-2">
              <div className="bg-background-card rounded-[4px] p-2">
                <p className="text-[12px] text-nowrap text-black dark:text-white font-medium">
                  Hover to play
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* outer div to maintain for placeholder if none of them is being rendered */}
      <div className="flex items-center min-h-[50px] h-auto md:h-[30px] md:w-[860px] my-2">
        {isEditing ? (
          <div className="flex flex-row gap-3 justify-end w-full">
            <Button
              variant="customSecondary"
              className="text-[14px] md:text-[14px] font-medium px-4 py-2 md:py-1 disabled:bg-transparent disabled:text-[#000F0847]/80"
              onClick={() => setIsEditing(false)}
              disabled={text === dialogue || isEditLoading}
            >
              Discard
            </Button>
            <Button
              variant="customPrimary"
              className="text-[14px] md:text-[14px] px-4 py-2 md:py-1 disabled:bg-[#002D1E11] disabled:text-[#000f08ab]"
              disabled={text === dialogue || isEditLoading}
              onClick={() => {
                if (isMobile) {
                  // For mobile, make sure to blur active elements to hide keyboard
                  const activeElement = document.activeElement as HTMLElement;
                  if (activeElement && activeElement.blur) {
                    activeElement.blur();
                  }
                  setAudioPlay(true);
                }
                handleEditMessage(index, text);
              }}
            >
              {isEditLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        ) : audioPlay ? (
          <div
            className={`transition-opacity duration-300 ease-in-out ${
              audioPlay ? 'opacity-100' : 'opacity-0'
            } w-full`}
          >
            <AudioCard 
              percent={percentagePlayed} 
              url={presigned_preview_url} 
              role={role}
            />
          </div>
        ) : (
          <div className="w-full" />
        )}
      </div>
    </div>
  );
};

export default DialogueCard;
